import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Engagement, EngagementWithDetails } from '../types/engagement';

export function useEngagements() {
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEngagements();
  }, []);

  async function fetchEngagements() {
    console.log('useEngagements - Fetching engagements');
    try {
      const { data, error } = await supabase
        .from('nrm_engagements')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('useEngagements - Error fetching engagements:', error);
        throw error;
      }
      
      console.log('useEngagements - Fetched engagements:', data);
      setEngagements(data || []);
    } catch (err) {
      console.error('useEngagements - Exception in fetchEngagements:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  async function createEngagement(data: Partial<Engagement>): Promise<Engagement> {
    try {
      const { data: newEngagement, error } = await supabase
        .from('nrm_engagements')
        .insert([data])
        .select()
        .single();

      if (error) throw error;

      setEngagements(prev => [newEngagement, ...prev]);
      return newEngagement;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to create engagement');
    }
  }

  async function updateEngagement(id: string, data: Partial<Engagement>): Promise<Engagement> {
    try {
      const { data: updatedEngagement, error } = await supabase
        .from('nrm_engagements')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      setEngagements(prev => prev.map(eng => 
        eng.id === id ? { ...eng, ...updatedEngagement } : eng
      ));
      return updatedEngagement;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to update engagement');
    }
  }

  async function deleteEngagement(engagementId: string): Promise<void> {
    try {
      // First get all contact IDs for this engagement
      const { data: contacts, error: contactsError } = await supabase
        .from('nrm_engagement_contacts')
        .select('contact_id')
        .eq('engagement_id', engagementId);

      if (contactsError) throw contactsError;

      // Delete contact step statuses for these contacts
      if (contacts && contacts.length > 0) {
        const contactIds = contacts.map(c => c.contact_id);
        const { error: statusError } = await supabase
          .from('nrm_contact_step_status')
          .delete()
          .in('contact_id', contactIds);

        if (statusError) throw statusError;
      }

      // Delete all engagement contacts
      const { error: contactError } = await supabase
        .from('nrm_engagement_contacts')
        .delete()
        .eq('engagement_id', engagementId);

      if (contactError) throw contactError;

      // Delete all engagement steps
      const { error: stepError } = await supabase
        .from('nrm_engagement_steps')
        .delete()
        .eq('engagement_id', engagementId);

      if (stepError) throw stepError;

      // Finally delete the engagement itself
      const { error } = await supabase
        .from('nrm_engagements')
        .delete()
        .eq('id', engagementId);

      if (error) throw error;

      // Update local state
      setEngagements(prev => prev.filter(eng => eng.id !== engagementId));
    } catch (error) {
      console.error('Error deleting engagement:', error);
      throw error;
    }
  }

  async function getEngagementDetails(id: string): Promise<EngagementWithDetails | null> {
    try {
      console.log('Fetching engagement details for id:', id);
      
      // Get engagement and its directly related data
      const { data: engagement, error: engagementError } = await supabase
        .from('nrm_engagements')
        .select(`
          *,
          contacts:nrm_engagement_contacts(contact_id),
          steps:nrm_engagement_steps(id, name, description, order_index)
        `)
        .eq('id', id)
        .single();

      if (engagementError) {
        console.error('Error fetching engagement:', engagementError);
        throw engagementError;
      }
      
      if (!engagement) {
        console.log('No engagement found for id:', id);
        return null;
      }

      // Get contact step statuses separately
      const { data: stepStatuses, error: statusError } = await supabase
        .from('nrm_contact_step_status')
        .select('*')
        .eq('engagement_id', id);

      if (statusError) {
        console.error('Error fetching step statuses:', statusError);
        throw statusError;
      }

      // Sort steps by order_index
      const sortedSteps = [...(engagement.steps || [])].sort((a, b) => a.order_index - b.order_index);

      console.log('Fetched engagement details:', {
        id: engagement.id,
        title: engagement.title,
        contactsCount: engagement.contacts?.length || 0,
        stepsCount: sortedSteps.length,
        statusCount: stepStatuses?.length || 0
      });

      // Get the latest status for each contact
      const contactLatestStatuses = (engagement.contacts || []).map((contact: { contact_id: string }) => {
        const contactStatuses = (stepStatuses || [])
          .filter(status => status.contact_id === contact.contact_id)
          .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime());
        return contactStatuses[0]; // Get the most recent status
      }).filter(Boolean);

      return {
        ...engagement,
        steps: sortedSteps,
        contactStatuses: contactLatestStatuses
      };
    } catch (err) {
      console.error('Error fetching engagement details:', err);
      throw err;
    }
  }

  async function addEngagementContact(engagementId: string, contactId: string) {
    console.log('Adding contact to engagement:', { engagementId, contactId });
    try {
      const { error } = await supabase
        .from('nrm_engagement_contacts')
        .insert({ 
          engagement_id: engagementId, 
          contact_id: contactId 
        });

      if (error) throw error;
    } catch (err) {
      console.error('Failed to add contact to engagement:', err);
      throw err;
    }
  }

  async function removeEngagementContact(engagementId: string, contactId: string) {
    console.log('Removing contact from engagement:', { engagementId, contactId });
    try {
      // First delete any step statuses for this contact
      const { error: statusError } = await supabase
        .from('nrm_contact_step_status')
        .delete()
        .match({ 
          engagement_id: engagementId, 
          contact_id: contactId 
        });

      if (statusError) throw statusError;

      // Then delete the engagement contact
      const { error } = await supabase
        .from('nrm_engagement_contacts')
        .delete()
        .match({ 
          engagement_id: engagementId, 
          contact_id: contactId 
        });

      if (error) throw error;
    } catch (err) {
      console.error('Failed to remove contact from engagement:', err);
      throw err;
    }
  }

  async function addEngagementStep(
    engagementId: string,
    data: { name: string; description?: string }
  ) {
    console.log('Adding step to engagement:', { engagementId, data });
    try {
      // Get current highest order_index
      const { data: steps, error: stepsError } = await supabase
        .from('nrm_engagement_steps')
        .select('order_index')
        .eq('engagement_id', engagementId)
        .order('order_index', { ascending: false })
        .limit(1);

      if (stepsError) throw stepsError;

      const nextOrderIndex = steps && steps.length > 0 ? steps[0].order_index + 1 : 0;

      const { error } = await supabase
        .from('nrm_engagement_steps')
        .insert({
          engagement_id: engagementId,
          name: data.name,
          description: data.description,
          order_index: nextOrderIndex
        });

      if (error) throw error;
    } catch (err) {
      console.error('Failed to add step to engagement:', err);
      throw err;
    }
  }

  async function removeEngagementStep(engagementId: string, stepId: string) {
    console.log('Removing step from engagement:', { engagementId, stepId });
    try {
      const { error } = await supabase
        .from('nrm_engagement_steps')
        .delete()
        .match({ id: stepId, engagement_id: engagementId });

      if (error) throw error;

      // Reorder remaining steps
      const { data: steps, error: stepsError } = await supabase
        .from('nrm_engagement_steps')
        .select('id, order_index')
        .eq('engagement_id', engagementId)
        .order('order_index');

      if (stepsError) throw stepsError;

      // Update order_index for each step
      for (let i = 0; i < steps.length; i++) {
        const { error: updateError } = await supabase
          .from('nrm_engagement_steps')
          .update({ order_index: i })
          .match({ id: steps[i].id });

        if (updateError) throw updateError;
      }
    } catch (err) {
      console.error('Failed to remove step from engagement:', err);
      throw err;
    }
  }

  async function reorderEngagementStep(
    engagementId: string,
    stepId: string,
    newIndex: number
  ) {
    console.log('Reordering step:', { engagementId, stepId, newIndex });
    try {
      // Get all steps to find the two steps we need to swap
      const { data: steps, error: stepsError } = await supabase
        .from('nrm_engagement_steps')
        .select('id, order_index')
        .eq('engagement_id', engagementId)
        .order('order_index');

      if (stepsError) throw stepsError;
      if (!steps) throw new Error('No steps found');

      const currentStep = steps.find(s => s.id === stepId);
      if (!currentStep) throw new Error('Step not found');
      
      const targetStep = steps[newIndex];
      if (!targetStep) throw new Error('Target position not found');

      console.log('Swapping steps:', {
        current: { id: currentStep.id, order_index: currentStep.order_index },
        target: { id: targetStep.id, order_index: targetStep.order_index }
      });

      // Call the database function to swap the steps atomically
      const { error } = await supabase.rpc('swap_engagement_step_order', {
        step1_id: currentStep.id,
        step2_id: targetStep.id
      });

      if (error) {
        console.error('Error swapping steps:', error);
        throw error;
      }

      console.log('Successfully swapped steps');
    } catch (err) {
      console.error('Failed to reorder step:', err);
      throw err;
    }
  }

  async function moveContactToStep(
    engagementId: string,
    contactId: string,
    toStepId: string | null
  ) {
    console.log('Moving contact to step:', { engagementId, contactId, toStepId });
    try {
      // Start a transaction to ensure data consistency
      const { data: existingStatuses, error: fetchError } = await supabase
        .from('nrm_contact_step_status')
        .select('step_id')
        .match({
          engagement_id: engagementId,
          contact_id: contactId
        });

      if (fetchError) throw fetchError;
      console.log('Existing statuses:', existingStatuses);

      // If moving to 'Not Started', delete all step statuses
      if (toStepId === null) {
        const { error: deleteError } = await supabase
          .from('nrm_contact_step_status')
          .delete()
          .match({
            engagement_id: engagementId,
            contact_id: contactId
          });

        if (deleteError) throw deleteError;
        console.log('Deleted all statuses');
      } else {
        // If this step already has a status, update its timestamp
        const hasExistingStatus = existingStatuses?.some(s => s.step_id === toStepId);
        
        if (hasExistingStatus) {
          const { error: updateError } = await supabase
            .from('nrm_contact_step_status')
            .update({ completed_at: new Date().toISOString() })
            .match({
              engagement_id: engagementId,
              contact_id: contactId,
              step_id: toStepId
            });

          if (updateError) throw updateError;
          console.log('Updated existing status');
        } else {
          // Insert new status for this step
          const { error: insertError } = await supabase
            .from('nrm_contact_step_status')
            .insert({
              engagement_id: engagementId,
              contact_id: contactId,
              step_id: toStepId,
              completed_at: new Date().toISOString()
            });

          if (insertError) throw insertError;
          console.log('Inserted new status');
        }
      }

      // Return the updated engagement details
      return await getEngagementDetails(engagementId);
    } catch (err) {
      console.error('Failed to move contact to step:', err);
      throw err;
    }
  }

  return {
    engagements,
    loading,
    error,
    refetch: fetchEngagements,
    createEngagement,
    updateEngagement,
    deleteEngagement,
    getEngagementDetails,
    addEngagementContact,
    removeEngagementContact,
    addEngagementStep,
    removeEngagementStep,
    reorderEngagementStep,
    moveContactToStep
  };
}
